import { useEffect, useState } from 'react';
import type { EditorState } from '@xsscool/shared';
import { submitPayload } from '../api-payloads';
import { useEditorState } from '../hooks/useEditorState';

interface Props {
  onClose: () => void;
  onSubmitted: (created: { id: string; status: string }) => void;
  // Optional override: when set, submit this state instead of the live
  // editor's current state. Used by "Submit snippet to library" in the Mine
  // panel: we hydrate an editor state from the saved snippet's payload and
  // pass it through unchanged.
  state?: EditorState;
  initialTitle?: string;
}

export function SubmitPayloadModal({ onClose, onSubmitted, state: stateOverride, initialTitle }: Props) {
  const liveState = useEditorState();
  const state = stateOverride ?? liveState;
  const [title, setTitle] = useState(initialTitle ?? '');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [alias, setAlias] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const created = await submitPayload({
        title: title.trim(),
        description: description.trim(),
        tags: tags.trim() || undefined,
        alias: alias.trim() || undefined,
        state,
      });
      onSubmitted(created);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="help-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="help-dialog submit-dialog" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="help-header">
          <strong>Submit payload</strong>
          <button type="button" className="help-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <p className="hint">
          Submit the current editor state for review. Approved payloads appear in the sidebar for
          everyone.
        </p>
        <label className="submit-field">
          <span>Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short, descriptive"
            maxLength={120}
            required
          />
        </label>
        <label className="submit-field">
          <span>Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this payload demonstrate?"
            rows={3}
            maxLength={500}
            required
          />
        </label>
        <label className="submit-field">
          <span>Tags <em>(optional, comma-separated)</em></span>
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g. xss, csp, oauth"
          />
        </label>
        <label className="submit-field">
          <span>Alias <em>(optional)</em></span>
          <input
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="Display name on accepted payloads"
            maxLength={60}
          />
        </label>
        {error && <p className="submit-error">{error}</p>}
        <div className="submit-actions">
          <button type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" disabled={submitting || !title.trim() || !description.trim()}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}
